import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, addMonths, subMonths, getDaysInMonth, differenceInDays } from 'date-fns';
import { ko } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, ExternalLink, Phone, Loader2, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { calendarApi } from '../api/calendar';
import { reservationsApi, Reservation, Room } from '../api/reservations';
import { roomsApi } from '../api/rooms';
import LoadingSpinner from '../components/common/LoadingSpinner';
import clsx from 'clsx';
import './CalendarPage.css';

// 모달용 기본 예약 정보 타입
interface ReservationInfo {
  reservationId: string;
  guestName: string;
  platform: string;
  checkinDate: string;
  checkoutDate: string;
  roomId: string;
  roomAddress: string;
  roomType: string;
}

// 영업일 계산 (새벽 2시 기준)
function getBusinessDate(): Date {
  const now = new Date();
  if (now.getHours() < 2) {
    return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
  return now;
}

// 플랫폼별 색상
function getPlatformClass(platform?: string): string {
  switch (platform?.toLowerCase()) {
    case 'yanolja':
    case '야놀자':
      return 'platform-yanolja';
    case 'airbnb':
    case '에어비앤비':
      return 'platform-airbnb';
    case 'yeogi':
    case '여기어때':
      return 'platform-yeogi';
    case 'offline':
    case '오프라인':
    case '직접예약':
      return 'platform-offline';
    default:
      return 'platform-default';
  }
}

// 요일 이름
const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

// 가격 포맷 함수
function formatPrice(price: number): string {
  return price.toLocaleString('ko-KR');
}

// 클립보드 복사 함수
async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // fallback for older browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  }
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showRoomNumber, setShowRoomNumber] = useState(true);
  const [showAccessDots, setShowAccessDots] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<ReservationInfo | null>(null);
  const [detailedReservation, setDetailedReservation] = useState<Reservation | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const daysInMonth = getDaysInMonth(currentDate);
  const todayStr = format(getBusinessDate(), 'yyyy-MM-dd');

  // 모달 편집 상태
  const [checkinMemo, setCheckinMemo] = useState('');
  const [checkoutMemo, setCheckoutMemo] = useState('');
  const [lateCheckout12, setLateCheckout12] = useState(false);
  const [lateCheckout1, setLateCheckout1] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // 객실 변경 상태
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [roomChangeReason, setRoomChangeReason] = useState('');
  const [isChangingRoom, setIsChangingRoom] = useState(false);

  // 토스트 알림 상태
  const [toast, setToast] = useState<string | null>(null);

  // 토스트 자동 숨김 (메모리 누수 방지를 위한 cleanup 포함)
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // 토스트 표시 함수
  const showToast = (message: string) => {
    setToast(message);
  };

  // 모달이 열릴 때 상세 정보 로드
  useEffect(() => {
    let isMounted = true;

    if (selectedReservation) {
      setIsLoadingDetail(true);

      // 예약 상세 정보와 사용 가능한 객실 목록 동시 로드
      const checkinDate = selectedReservation.checkinDate?.split('T')[0] || todayStr;

      Promise.all([
        reservationsApi.getReservationById(selectedReservation.reservationId),
        reservationsApi.getAvailableRooms(checkinDate),
        roomsApi.getAllRooms(),
      ])
        .then(([data, availRooms, allRoomsList]) => {
          if (!isMounted) return;

          setDetailedReservation(data);
          setAvailableRooms(availRooms);
          // 전체 객실 중 available 상태만 필터 (none 제외), 캘린더 테이블 순서로 정렬 (roomType → roomId)
          setAllRooms(
            allRoomsList
              .filter(r => r.status === 'available' && r.roomId !== 'none')
              .sort((a, b) => {
                // roomType 먼저 비교
                const typeCompare = (a.roomType || '').localeCompare(b.roomType || '');
                if (typeCompare !== 0) return typeCompare;
                // 같은 타입이면 roomId로 비교
                return (a.roomId || '').localeCompare(b.roomId || '');
              })
          );
          setSelectedRoomId(data.roomId);
          setRoomChangeReason('');
          setShowAllRooms(false);

          // 메모 필드 초기화
          setCheckinMemo(data.checkinMemo || '');
          setCheckoutMemo(data.checkoutMemo || '');

          // 레이트체크아웃 체크박스 초기화
          const timeStr = data.lateCheckoutTime || '';
          const memo = data.checkoutMemo || '';

          // DB isLateCheckout 또는 메모에서 확인
          if (data.isLateCheckout) {
            // lateCheckoutTime에서 12시/13시 확인 (다양한 형식 지원)
            const is12 = timeStr.includes('12:00') || timeStr.includes('T12:');
            const is13 = timeStr.includes('13:00') || timeStr.includes('T13:');
            setLateCheckout12(is12);
            setLateCheckout1(is13);
          } else {
            // 메모에서 확인 (fallback)
            setLateCheckout12(memo.includes('레이트12시'));
            setLateCheckout1(memo.includes('레이트1시'));
          }
        })
        .catch(err => {
          console.error('Failed to load reservation details:', err);
        })
        .finally(() => {
          if (isMounted) {
            setIsLoadingDetail(false);
          }
        });
    } else {
      setDetailedReservation(null);
      setAvailableRooms([]);
      setAllRooms([]);
      setShowAllRooms(false);
      setSelectedRoomId('');
      setRoomChangeReason('');
      setCheckinMemo('');
      setCheckoutMemo('');
      setLateCheckout12(false);
      setLateCheckout1(false);
    }

    return () => { isMounted = false; };
  }, [selectedReservation, todayStr]);

  // 메모 정리 함수
  const cleanupMemo = (memo: string): string => {
    return memo
      .replace(/,\s*,+/g, ',')  // 연속 쉼표 제거
      .replace(/^[\s,]+/, '')    // 앞쪽 쉼표/공백 제거
      .replace(/[\s,]+$/, '')    // 뒤쪽 쉼표/공백 제거
      .trim();
  };

  // 레이트체크아웃 체크박스 변경 처리
  const handleLateCheckout12Change = (checked: boolean) => {
    setLateCheckout12(checked);
    if (checked) {
      setLateCheckout1(false); // 12시 선택시 1시 해제
      // 메모에서 레이트1시 제거하고 레이트12시 추가
      let newMemo = checkoutMemo.replace(/레이트1시/g, '');
      newMemo = cleanupMemo(newMemo);
      if (!newMemo.includes('레이트12시')) {
        newMemo = newMemo ? `${newMemo}, 레이트12시` : '레이트12시';
      }
      setCheckoutMemo(cleanupMemo(newMemo));
    } else {
      // 메모에서 레이트12시 제거
      const newMemo = checkoutMemo.replace(/레이트12시/g, '');
      setCheckoutMemo(cleanupMemo(newMemo));
    }
  };

  const handleLateCheckout1Change = (checked: boolean) => {
    setLateCheckout1(checked);
    if (checked) {
      setLateCheckout12(false); // 1시 선택시 12시 해제
      // 메모에서 레이트12시 제거하고 레이트1시 추가
      let newMemo = checkoutMemo.replace(/레이트12시/g, '');
      newMemo = cleanupMemo(newMemo);
      if (!newMemo.includes('레이트1시')) {
        newMemo = newMemo ? `${newMemo}, 레이트1시` : '레이트1시';
      }
      setCheckoutMemo(cleanupMemo(newMemo));
    } else {
      // 메모에서 레이트1시 제거
      const newMemo = checkoutMemo.replace(/레이트1시/g, '');
      setCheckoutMemo(cleanupMemo(newMemo));
    }
  };

  // 업데이트 함수
  const handleUpdateReservation = async () => {
    if (!selectedReservation) return;

    setIsUpdating(true);
    try {
      // 레이트체크아웃 설정
      const isLateCheckout = lateCheckout12 || lateCheckout1;
      let lateCheckoutTime: string | undefined = undefined;
      if (lateCheckout12) {
        lateCheckoutTime = '12:00';
      } else if (lateCheckout1) {
        lateCheckoutTime = '13:00';
      }

      await reservationsApi.updateReservation(selectedReservation.reservationId, {
        checkinMemo,
        checkoutMemo,
        isLateCheckout,
        lateCheckoutTime: isLateCheckout ? lateCheckoutTime : undefined,
      });
      // 성공 시 상세 정보 다시 로드
      const updated = await reservationsApi.getReservationById(selectedReservation.reservationId);
      setDetailedReservation(updated);
      showToast('저장됨');
    } catch (err) {
      console.error('Failed to update reservation:', err);
      showToast('저장 실패');
    } finally {
      setIsUpdating(false);
    }
  };

  // 연박처리 핸들러
  const handleExtendedStay = async () => {
    if (!selectedReservation || !detailedReservation) return;

    const roomAddress = selectedReservation.roomAddress.toUpperCase();
    const guestName = selectedReservation.guestName;

    // 확인 다이얼로그
    if (!window.confirm(`${roomAddress}, ${guestName} 연박처리하시겠습니까?`)) {
      return;
    }

    setIsUpdating(true);
    try {
      // 1. checkinMemo에서 (C/I=xx:00) 패턴을 (C/I=11:00)으로 변경
      let newMemo = checkinMemo.replace(/\(C\/I=\d{1,2}:\d{2}\)/g, '(C/I=11:00)');

      // 2. "❌ 연박(청소취소)" 추가 (중복 방지)
      if (!newMemo.includes('연박(청소취소)')) {
        newMemo = newMemo ? `${newMemo}, ❌ 연박(청소취소)` : '❌ 연박(청소취소)';
      }
      newMemo = cleanupMemo(newMemo);

      // 3. API 호출 - checkinTime과 checkinMemo 업데이트
      await reservationsApi.updateReservation(selectedReservation.reservationId, {
        checkinTime: '11:00',
        checkinMemo: newMemo,
      });

      // 상태 업데이트
      setCheckinMemo(newMemo);
      const updated = await reservationsApi.getReservationById(selectedReservation.reservationId);
      setDetailedReservation(updated);
      showToast('연박처리 완료');
    } catch (err) {
      console.error('Failed to process extended stay:', err);
      showToast('연박처리 실패');
    } finally {
      setIsUpdating(false);
    }
  };

  // 객실 변경 핸들러
  const handleRoomChange = async () => {
    if (!selectedReservation || !selectedRoomId) return;
    if (selectedRoomId === detailedReservation?.roomId) {
      showToast('같은 객실입니다');
      return;
    }

    setIsChangingRoom(true);
    try {
      await reservationsApi.updateRoom(
        selectedReservation.reservationId,
        selectedRoomId,
        roomChangeReason || undefined
      );
      // 성공 시 상세 정보 다시 로드
      const updated = await reservationsApi.getReservationById(selectedReservation.reservationId);
      setDetailedReservation(updated);
      setRoomChangeReason('');
      // 캘린더도 갱신
      refetch();
      showToast('객실 변경됨');
    } catch (err) {
      console.error('Failed to change room:', err);
      showToast('객실 변경 실패');
    } finally {
      setIsChangingRoom(false);
    }
  };

  const { data: calendarData, isLoading, refetch } = useQuery({
    queryKey: ['calendar', year, month],
    queryFn: () => calendarApi.getMonthlyCalendar(year, month),
  });

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToCurrentMonth = () => setCurrentDate(new Date());

  // 객실 타입별 그룹핑 (none 타입 제외, 알파벳 순 정렬)
  const groupedRoomsUnsorted = calendarData?.rooms.reduce((acc, room) => {
    const type = room.roomType || 'Other';
    // none 타입은 제외
    if (type.toLowerCase() === 'none' || room.roomId === 'none') {
      return acc;
    }
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(room);
    return acc;
  }, {} as Record<string, NonNullable<typeof calendarData>['rooms']>) || {};

  // 알파벳 순으로 정렬하고, 그룹 내 객실은 room_id 순으로 정렬
  const groupedRooms = Object.keys(groupedRoomsUnsorted)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, key) => {
      // 그룹 내 객실을 room_id 기준으로 정렬
      acc[key] = groupedRoomsUnsorted[key].sort((a, b) =>
        a.roomId.localeCompare(b.roomId)
      );
      return acc;
    }, {} as Record<string, NonNullable<typeof calendarData>['rooms']>);

  // 중복 예약 개수 계산
  const duplicateCount = calendarData?.rooms.reduce((count, room) => {
    return count + room.days.filter(day => day.reservations.length > 1).length;
  }, 0) || 0;

  const isCurrentMonth =
    currentDate.getFullYear() === new Date().getFullYear() &&
    currentDate.getMonth() === new Date().getMonth();

  return (
    <div className="calendar-shell">
      {/* Date navigation - 대시보드 스타일 */}
      <div className={clsx('card mb-3', isCurrentMonth && 'bg-blue-50')}>
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-1">
            <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>

            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <DatePicker
              selected={currentDate}
              onChange={(date: Date | null) => date && setCurrentDate(date)}
              dateFormat="yyyy년 M월"
              showMonthYearPicker
              locale={ko}
              popperClassName="calendar-datepicker-popper"
              customInput={
                <button className="text-lg font-semibold hover:text-blue-600 cursor-pointer whitespace-nowrap">
                  {format(currentDate, 'yyyy년 M월', { locale: ko })}
                </button>
              }
            />

            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="ml-3">
            {isCurrentMonth ? (
              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-cyan-500 text-white whitespace-nowrap">
                이번 달
              </span>
            ) : (
              <button onClick={goToCurrentMonth} className="btn btn-outline text-sm py-1 whitespace-nowrap">
                이번 달
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="legend-container">
        <div className="legend-item legend-platform">
          <div className="legend-color" style={{ backgroundColor: '#e84560' }}></div>
          <span className="legend-text">야놀자</span>
        </div>
        <div className="legend-item legend-platform">
          <div className="legend-color" style={{ backgroundColor: '#39bbc6' }}></div>
          <span className="legend-text">에어비앤비</span>
        </div>
        <div className="legend-item legend-platform">
          <div className="legend-color" style={{ backgroundColor: '#f5b116' }}></div>
          <span className="legend-text">여기어때</span>
        </div>
        <div className="legend-item legend-platform">
          <div className="legend-color" style={{ backgroundColor: '#afbfcf' }}></div>
          <span className="legend-text">오프라인</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#000000' }}></div>
          <span className="legend-text">중복 예약({duplicateCount})</span>
        </div>
        <div className="legend-item">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={showRoomNumber}
              onChange={(e) => setShowRoomNumber(e.target.checked)}
              className="form-checkbox"
            />
            <span className="legend-text">객실번호</span>
          </label>
        </div>
        <div className="legend-item">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={showAccessDots}
              onChange={(e) => setShowAccessDots(e.target.checked)}
              className="form-checkbox"
            />
            <span className="legend-text">접속여부</span>
            <span className="access-dot-legend parking"></span>
            <span className="access-dot-legend checkin"></span>
          </label>
        </div>
      </div>

      {/* Calendar Grid */}
      {isLoading ? (
        <LoadingSpinner className="py-16" />
      ) : calendarData ? (
        <div className="calendar-grid-container">
          {/* 날짜 헤더 행 */}
          <div
            className="calendar-header-row"
            style={{ minWidth: `calc(var(--room-id-width) + var(--cell-width) * ${daysInMonth})` }}
          >
            <div className="room-id-header-cell">객실</div>
            <div className="date-headers-grid">
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const date = new Date(year, month - 1, day);
                const dayOfWeek = date.getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isToday = dateStr === todayStr;

                return (
                  <div
                    key={day}
                    className={clsx(
                      'date-header-cell',
                      isWeekend && 'weekend',
                      isToday && 'today'
                    )}
                  >
                    <div className={clsx('day-header', isWeekend && 'text-red-500')}>
                      {day}
                    </div>
                    <small className={clsx('day-name', dayOfWeek === 0 && 'text-red-500', dayOfWeek === 6 && 'text-blue-500')}>
                      ({dayNames[dayOfWeek]})
                    </small>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 본문 스크롤 영역 */}
          <div className="calendar-body-scroll">
            {Object.entries(groupedRooms).map(([roomType, rooms]) => (
              <div key={`group-${roomType}`} className="room-group-section">
                {/* Room Type Group Header */}
                <div
                  className="room-group-header"
                  style={{ minWidth: `calc(var(--room-id-width) + var(--cell-width) * ${daysInMonth})` }}
                >
                  <div className="room-group-id-cell"></div>
                  <div className="room-group-header-content">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="room-group-header-label">{roomType}</span>
                    ))}
                  </div>
                </div>

                {/* Room Rows */}
                {rooms.map((room) => {
                  // 예약 셀 렌더링을 위한 데이터 준비
                  const reservationCells: Array<{
                    startDay: number;
                    span: number;
                    reservation: NonNullable<typeof room.days[0]['reservations'][0]>;
                    isDuplicate: boolean;
                  }> = [];

                  const processedDays = new Set<number>();

                  room.days.forEach((day, dayIndex) => {
                    const dayNum = dayIndex + 1;
                    if (processedDays.has(dayNum)) return;

                    const hasReservation = day.reservations.length > 0;
                    const isDuplicate = day.reservations.length > 1;

                    if (hasReservation) {
                      const reservation = day.reservations[0];

                      // 연속일 계산 (중복이 아닌 경우만)
                      let span = 1;
                      if (!isDuplicate && reservation) {
                        for (let nextDay = dayNum + 1; nextDay <= daysInMonth; nextDay++) {
                          const nextDayData = room.days[nextDay - 1];
                          if (
                            nextDayData &&
                            nextDayData.reservations.length === 1 &&
                            nextDayData.reservations[0]?.reservationId === reservation.reservationId
                          ) {
                            span++;
                            processedDays.add(nextDay);
                          } else {
                            break;
                          }
                        }
                      }

                      if (reservation) {
                        reservationCells.push({
                          startDay: dayNum,
                          span,
                          reservation,
                          isDuplicate,
                        });
                      }
                    }
                  });

                  return (
                    <div
                      key={room.roomId}
                      className="room-row"
                      style={{ minWidth: `calc(var(--room-id-width) + var(--cell-width) * ${daysInMonth})` }}
                    >
                      <div className="room-id-cell">
                        {showRoomNumber ? (
                          <span className="room-address-text">{room.roomAddress?.toUpperCase()}</span>
                        ) : (
                          <span className="room-id-text">{room.roomId.toUpperCase()}</span>
                        )}
                      </div>
                      <div
                        className="room-day-cells-grid"
                        style={{ gridTemplateColumns: `repeat(${daysInMonth}, var(--cell-width))` }}
                      >
                        {/* 배경 셀들 */}
                        {Array.from({ length: daysInMonth }, (_, i) => {
                          const dayNum = i + 1;
                          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                          const date = new Date(year, month - 1, dayNum);
                          const dayOfWeek = date.getDay();
                          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                          const isToday = dateStr === todayStr;

                          return (
                            <div
                              key={`bg-${dayNum}`}
                              className={clsx('day-cell-bg', isWeekend && 'weekend', isToday && 'today')}
                              style={{ gridColumn: dayNum }}
                            />
                          );
                        })}

                        {/* 예약 셀들 (gridColumn으로 span 처리) */}
                        {reservationCells.map(({ startDay, span, reservation, isDuplicate }) => (
                          <div
                            key={`res-${reservation.reservationId}-${startDay}`}
                            onClick={() => setSelectedReservation({
                              reservationId: reservation.reservationId,
                              guestName: reservation.guestName,
                              platform: reservation.platform,
                              checkinDate: reservation.checkinDate,
                              checkoutDate: reservation.checkoutDate,
                              roomId: room.roomId,
                              roomAddress: room.roomAddress,
                              roomType: room.roomType,
                            })}
                            className={clsx(
                              'reservation-cell',
                              isDuplicate && 'duplicate',
                              !isDuplicate && getPlatformClass(reservation.platform)
                            )}
                            style={{ gridColumn: `${startDay} / span ${span}` }}
                          >
                            <span className="reservation-name">{reservation.guestName}</span>
                            {/* 접속여부 점 표시 (미접속 시에만 표시) */}
                            {showAccessDots && (
                              <>
                                {!reservation.readParkingWebpage && (
                                  <span className="access-dot parking" title="주차등록 미접속"></span>
                                )}
                                {!reservation.readCheckinWebpage && (
                                  <span className="access-dot checkin" title="체크인안내 미접속"></span>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Reservation Detail Modal */}
      {selectedReservation && (
        <>
          <div
            className="reservation-modal-overlay"
            onClick={() => setSelectedReservation(null)}
          />
          <div className="reservation-modal">
            <div className="reservation-modal-header">
              <h3>{selectedReservation.guestName}님의 예약</h3>
              <button
                className="reservation-modal-close"
                onClick={() => setSelectedReservation(null)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="reservation-modal-content">
              {isLoadingDetail ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : (
                <>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">예약번호</span>
                    <span
                      className="reservation-modal-value text-xs text-gray-500 copyable"
                      onClick={() => {
                        copyToClipboard(selectedReservation.reservationId);
                        showToast('예약번호 복사됨');
                      }}
                      title="클릭하여 복사"
                    >
                      {selectedReservation.reservationId}
                      <Copy className="w-3 h-3 ml-1 inline-block opacity-50" />
                    </span>
                  </div>
                  {/* 객실 변경 섹션 */}
                  <div className="reservation-modal-room-change">
                    <div className="reservation-modal-row">
                      <span className="reservation-modal-label">현재 객실</span>
                      <span className="reservation-modal-value">
                        [{detailedReservation?.roomId?.toUpperCase() || selectedReservation.roomId?.toUpperCase()}] {detailedReservation?.room?.roomAddress?.toUpperCase() || selectedReservation.roomAddress?.toUpperCase()} ({selectedReservation.roomType})
                      </span>
                    </div>
                    <div className="room-change-form">
                      {/* 빈 객실 / 전체 라디오 버튼 */}
                      <div className="room-filter-radio">
                        <label className="room-filter-label">
                          <input
                            type="radio"
                            name="roomFilter"
                            checked={!showAllRooms}
                            onChange={() => setShowAllRooms(false)}
                          />
                          <span>빈 객실</span>
                        </label>
                        <label className="room-filter-label">
                          <input
                            type="radio"
                            name="roomFilter"
                            checked={showAllRooms}
                            onChange={() => setShowAllRooms(true)}
                          />
                          <span>전체</span>
                        </label>
                      </div>
                      <div className="room-change-select-row">
                        <select
                          className="room-change-select"
                          value={selectedRoomId}
                          onChange={(e) => setSelectedRoomId(e.target.value)}
                        >
                          <option value={detailedReservation?.roomId || ''}>
                            [{detailedReservation?.roomId?.toUpperCase()}] {detailedReservation?.room?.roomAddress?.toUpperCase() || selectedReservation.roomAddress?.toUpperCase()} (현재)
                          </option>
                          {(() => {
                            // 타입별 그룹핑
                            const roomsToShow = showAllRooms ? allRooms : availableRooms;
                            const filteredRooms = roomsToShow.filter(room => room.roomId !== detailedReservation?.roomId);
                            const grouped = filteredRooms.reduce((acc, room) => {
                              const type = room.roomType || 'Other';
                              if (!acc[type]) acc[type] = [];
                              acc[type].push(room);
                              return acc;
                            }, {} as Record<string, Room[]>);
                            const types = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

                            return types.map(type => (
                              <optgroup key={type} label={type}>
                                {grouped[type].map(room => {
                                  const isAvailable = availableRooms.some(r => r.roomId === room.roomId);
                                  return (
                                    <option key={room.roomId} value={room.roomId}>
                                      {showAllRooms && isAvailable ? '* ' : ''}{room.roomAddress?.toUpperCase()}
                                    </option>
                                  );
                                })}
                              </optgroup>
                            ));
                          })()}
                        </select>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={handleRoomChange}
                          disabled={isChangingRoom || selectedRoomId === detailedReservation?.roomId}
                        >
                          {isChangingRoom ? <Loader2 className="w-4 h-4 animate-spin" /> : '변경'}
                        </button>
                      </div>
                      <input
                        type="text"
                        className="room-change-reason"
                        placeholder="변경 사유 (선택)"
                        value={roomChangeReason}
                        onChange={(e) => setRoomChangeReason(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">플랫폼</span>
                    <span className={clsx(
                      'reservation-modal-value reservation-modal-platform',
                      getPlatformClass(selectedReservation.platform)
                    )}>
                      {selectedReservation.platform}
                    </span>
                  </div>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">체크인</span>
                    <span className="reservation-modal-value">
                      {selectedReservation.checkinDate?.split('T')[0] &&
                        format(new Date(selectedReservation.checkinDate.split('T')[0]), 'yyyy년 M월 d일 (E)', { locale: ko })}
                    </span>
                  </div>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">체크아웃</span>
                    <span className="reservation-modal-value">
                      {selectedReservation.checkoutDate?.split('T')[0] &&
                        format(new Date(selectedReservation.checkoutDate.split('T')[0]), 'yyyy년 M월 d일 (E)', { locale: ko })}
                    </span>
                  </div>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">DAYS</span>
                    <span className="reservation-modal-value">
                      {detailedReservation?.nights ||
                        (selectedReservation.checkinDate && selectedReservation.checkoutDate
                          ? differenceInDays(
                              new Date(selectedReservation.checkoutDate.split('T')[0]),
                              new Date(selectedReservation.checkinDate.split('T')[0])
                            )
                          : '-')}박
                    </span>
                  </div>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">판매가격</span>
                    <span className="reservation-modal-value">
                      {detailedReservation?.sellingPrice
                        ? `${formatPrice(detailedReservation.sellingPrice)}원`
                        : '-'}
                    </span>
                  </div>
                  <div className="reservation-modal-row">
                    <span className="reservation-modal-label">연락처</span>
                    <span className="reservation-modal-value flex items-center gap-1">
                      {detailedReservation?.guestPhone ? (
                        <span
                          className="copyable flex items-center gap-1"
                          onClick={() => {
                            copyToClipboard(detailedReservation.guestPhone || '');
                            showToast('전화번호 복사됨');
                          }}
                          title="클릭하여 복사"
                        >
                          <Phone className="w-3 h-3" />
                          {detailedReservation.guestPhone}
                          <Copy className="w-3 h-3 opacity-50" />
                        </span>
                      ) : '-'}
                    </span>
                  </div>

                  {/* 레이트체크아웃 + 연박처리 */}
                  <div className="reservation-modal-late-checkout">
                    <label className="reservation-modal-checkbox">
                      <input
                        type="checkbox"
                        checked={lateCheckout12}
                        onChange={(e) => handleLateCheckout12Change(e.target.checked)}
                      />
                      <span>레이트12시</span>
                    </label>
                    <label className="reservation-modal-checkbox">
                      <input
                        type="checkbox"
                        checked={lateCheckout1}
                        onChange={(e) => handleLateCheckout1Change(e.target.checked)}
                      />
                      <span>레이트1시</span>
                    </label>
                    <button
                      type="button"
                      className="extended-stay-button"
                      onClick={handleExtendedStay}
                      disabled={isUpdating}
                    >
                      연박
                    </button>
                  </div>

                  {/* 체크인 메모 (수정 가능) */}
                  <div className="reservation-modal-memo">
                    <span className="reservation-modal-label">체크인 특이사항</span>
                    <input
                      type="text"
                      className="reservation-modal-input"
                      value={checkinMemo}
                      onChange={(e) => setCheckinMemo(e.target.value)}
                      placeholder="체크인 메모 입력"
                    />
                    <div className="quick-add-buttons">
                      {[
                        { label: '추가1명', text: '추가1명' },
                        { label: '싱글', text: '싱글매트리스' },
                        { label: '추가2명', text: '추가2명' },
                        { label: '퀸', text: '퀸매트리스' },
                        { label: '세탁세제', text: '세탁세제' },
                      ].map(({ label, text }) => (
                        <button
                          key={label}
                          type="button"
                          className="quick-add-btn"
                          onClick={() => {
                            if (!checkinMemo.includes(text)) {
                              const newMemo = checkinMemo ? `${checkinMemo}, ${text}` : text;
                              setCheckinMemo(cleanupMemo(newMemo));
                            } else {
                              showToast('이미 추가됨');
                            }
                          }}
                        >
                          {label}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="quick-add-btn quick-add-reset"
                        onClick={() => {
                          let newMemo = checkinMemo;
                          ['추가1명', '추가2명', '퀸매트리스', '싱글매트리스', '세탁세제'].forEach(text => {
                            newMemo = newMemo.replace(new RegExp(text, 'g'), '');
                          });
                          setCheckinMemo(cleanupMemo(newMemo));
                          showToast('초기화됨');
                        }}
                      >
                        초기화
                      </button>
                    </div>
                  </div>

                  {/* 체크아웃 메모 (수정 가능) */}
                  <div className="reservation-modal-memo">
                    <span className="reservation-modal-label">체크아웃 특이사항</span>
                    <input
                      type="text"
                      className="reservation-modal-input"
                      value={checkoutMemo}
                      onChange={(e) => setCheckoutMemo(e.target.value)}
                      placeholder="체크아웃 메모 입력"
                    />
                  </div>

                  {detailedReservation?.specialRequests && (
                    <div className="reservation-modal-memo">
                      <span className="reservation-modal-label">특이사항</span>
                      <p className="reservation-modal-memo-text">{detailedReservation.specialRequests}</p>
                    </div>
                  )}

                  {/* 객실 변경 이력 */}
                  {detailedReservation?.roomAssignmentHistory && detailedReservation.roomAssignmentHistory.length > 0 && (
                    <div className="reservation-modal-log-section">
                      <span className="reservation-modal-log-title">객실 변경 이력</span>
                      <ul className="reservation-modal-log-list">
                        {detailedReservation.roomAssignmentHistory.map((entry: any, idx: number) => (
                          <li key={idx} className="reservation-modal-log-item">
                            <span className="log-date">
                              {entry.changed_at}
                            </span>
                            <span className="log-content">
                              {entry.previous_room_id?.toUpperCase()} → {entry.new_room_id?.toUpperCase()}
                              {entry.reason && entry.reason !== 'none' && <span className="log-reason">({entry.reason})</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 객실안내 접속 로그 */}
                  {detailedReservation?.readCheckinWebpageLog && detailedReservation.readCheckinWebpageLog.length > 0 && (
                    <div className="reservation-modal-log-section">
                      <span className="reservation-modal-log-title">
                        객실안내 접속 로그
                        {detailedReservation.readCheckinWebpage && <span className="log-badge log-badge-success">확인</span>}
                      </span>
                      <ul className="reservation-modal-log-list">
                        {detailedReservation.readCheckinWebpageLog.map((entry, idx) => (
                          <li key={idx} className="reservation-modal-log-item">
                            <span className="log-date">
                              {entry.date && format(new Date(entry.date), 'M/d HH:mm', { locale: ko })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* 주차등록 접속 로그 */}
                  {(detailedReservation?.readParkingWebpageLog?.length || detailedReservation?.parkingRegistrationLog?.length) ? (
                    <div className="reservation-modal-log-section">
                      <span className="reservation-modal-log-title">
                        주차등록 로그
                        {detailedReservation.readParkingWebpage && <span className="log-badge log-badge-success">확인</span>}
                      </span>
                      <ul className="reservation-modal-log-list">
                        {detailedReservation.readParkingWebpageLog?.map((entry, idx) => (
                          <li key={`parking-view-${idx}`} className="reservation-modal-log-item">
                            <span className="log-date">
                              {entry.date && format(new Date(entry.date), 'M/d HH:mm', { locale: ko })}
                            </span>
                            <span className="log-content log-view">페이지 접속</span>
                          </li>
                        ))}
                        {detailedReservation.parkingRegistrationLog?.map((entry, idx) => (
                          <li key={`parking-reg-${idx}`} className="reservation-modal-log-item">
                            <span className="log-date">
                              {entry.date && format(new Date(entry.date), 'M/d HH:mm', { locale: ko })}
                            </span>
                            <span className="log-content log-register">차량 등록</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <div className="reservation-modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setSelectedReservation(null)}
              >
                닫기
              </button>
              <button
                className="btn btn-success flex items-center gap-1"
                onClick={handleUpdateReservation}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                업데이트
              </button>
              <button
                className="btn btn-primary flex items-center gap-1"
                onClick={() => navigate(`/reservations/${selectedReservation.reservationId}`)}
              >
                <ExternalLink className="w-4 h-4" />
                예약 관리
              </button>
            </div>
          </div>
        </>
      )}

      {/* 토스트 알림 */}
      {toast && (
        <div className="toast-notification">
          {toast}
        </div>
      )}
    </div>
  );
}
